import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import sh from "bundles/common/utils/sh";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const COMPARISON_LABELS = {
  daily: "vs yesterday",
  weekly: "vs last week",
  monthly: "vs last month",
  custom: "vs prev. period",
};

const labelSx = {
  color: "hsl(150 12% 38%)",
  fontSize: 12,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 0.5,
};

const valueSx = {
  color: "hsl(150 30% 12%)",
  fontSize: 26,
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  lineHeight: 1.2,
};

const cardSx = {
  border: "1px solid hsl(140 8% 88%)",
  borderRadius: "0.75rem",
  backgroundColor: "#fff",
  p: 2.5,
};

const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 12% 38%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "12px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

function formatDuration(secs) {
  if (!secs || secs === 0) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function formatHours(secs) {
  if (!secs || secs === 0) return "0h";
  const h = (secs / 3600).toFixed(1);
  return `${h}h`;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function DeltaBadge({ delta, comparisonLabel }) {
  if (delta === null || delta === undefined) {
    return comparisonLabel ? (
      <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.4)" }}>No prior data</Typography>
    ) : null;
  }
  const positive = delta >= 0;
  const color = positive ? "hsl(142 64% 33%)" : "hsl(0 65% 47%)";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
        {positive ? "↑" : "↓"} {Math.abs(delta)}%
      </Typography>
      {comparisonLabel && (
        <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.45)" }}>{comparisonLabel}</Typography>
      )}
    </Box>
  );
}

function KpiCard({ label, value, highlighted, delta, comparisonLabel, footer }) {
  const showDelta = delta !== undefined;
  return (
    <Box sx={{ ...cardSx, ...(highlighted && { backgroundColor: "hsl(140 10% 95%)" }), height: "100%" }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={valueSx}>{value ?? "—"}</Typography>
      {showDelta && (
        <Box sx={{ mt: 0.75 }}>
          <DeltaBadge delta={delta} comparisonLabel={comparisonLabel} />
        </Box>
      )}
      {footer && <Box sx={{ mt: 1 }}>{footer}</Box>}
    </Box>
  );
}

function renderTaskUserAvatars(users, size = 20) {
  if (!users || users.length === 0) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {users.slice(0, 4).map((u) => (
        <Tooltip key={u.id} title={u.full_name || "Unknown"} arrow placement="top">
          <UserAvatar
            user={u}
            size={size}
            sx={{ ml: "-4px", "&:first-of-type": { ml: 0 } }}
            tooltip={false}
          />
        </Tooltip>
      ))}
      {users.length > 4 && (
        <Box component="span" sx={{ fontSize: 11, color: "rgba(42, 51, 78, 0.5)", ml: 0.5 }}>
          +{users.length - 4}
        </Box>
      )}
    </Box>
  );
}

function ProjectRow({ project, expanded, onToggle }) {
  const tasks = project.task_breakdown || [];
  const hasTasks = tasks.length > 0;
  const totalTaskSecs = tasks.reduce((sum, t) => sum + (t.total_secs || 0), 0);

  return (
    <>
      <TableRow
        hover
        onClick={hasTasks ? onToggle : undefined}
        sx={{
          cursor: hasTasks ? "pointer" : "default",
          "&:hover": { backgroundColor: "hsl(140 8% 97%)" },
        }}
      >
        <TableCell sx={{ ...tableBodyCellSx, width: 36, px: 1 }}>
          {hasTasks && (
            <IconButton size="small" sx={{ color: "hsl(150 12% 38%)", p: 0 }}>
              {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={tableBodyCellSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: project.color || "#ccc", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
              {project.name || "—"}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ ...tableBodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {formatDuration(project.total_duration_secs)}
        </TableCell>
        <TableCell sx={tableBodyCellSx}>{project.total_entries ?? 0}</TableCell>
        <TableCell sx={tableBodyCellSx}>{formatDuration(project.week_duration)}</TableCell>
        <TableCell sx={tableBodyCellSx}>{formatDuration(project.month_duration)}</TableCell>
        <TableCell sx={tableBodyCellSx}>{formatRelativeTime(project.last_activity_at)}</TableCell>
      </TableRow>
      {hasTasks && (
        <TableRow>
          <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded ? "1px solid hsl(140 8% 88%)" : "none" }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  backgroundColor: "hsl(140 10% 97%)",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                  gap: 1.5,
                }}
              >
                {tasks.map((task) => (
                  <TaskCard key={task.task_name} task={task} color={project.color} totalTaskSecs={totalTaskSecs} />
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function TaskCard({ task, color, totalTaskSecs }) {
  const pct = totalTaskSecs > 0 ? Math.round((task.total_secs / totalTaskSecs) * 100) : 0;
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "0.75rem",
        backgroundColor: "#fff",
        border: "1px solid hsl(140 8% 90%)",
        "&:hover": {
          borderColor: color || "hsl(140 8% 82%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        },
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color || "#ccc", flexShrink: 0 }} />
        <Typography sx={{ flex: 1, fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#2A334E" }}>
          {task.task_name}
        </Typography>
        <Box sx={{ flexShrink: 0 }}>{renderTaskUserAvatars(task.users)}</Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: 18, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#2A334E", lineHeight: 1 }}>
          {formatDuration(task.total_secs)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.5)" }}>
          · {task.entry_count} {task.entry_count === 1 ? "entry" : "entries"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.04)", overflow: "hidden" }}>
          <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 2, bgcolor: color || "#ccc", opacity: 0.7, transition: "width 0.3s ease" }} />
        </Box>
        <Typography sx={{ fontSize: 10, color: "rgba(42,51,78,0.45)", flexShrink: 0 }}>{pct}%</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.55)" }}>{formatDuration(task.avg_entry_secs)} avg</Typography>
        <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.4)" }}>{formatRelativeTime(task.last_activity_at)}</Typography>
      </Box>
    </Box>
  );
}

function TaskTable({ project }) {
  const tasks = project.task_breakdown || [];
  const totalSecs = project.total_duration_secs || tasks.reduce((sum, t) => sum + (t.total_secs || 0), 0);

  if (tasks.length === 0) return null;

  return (
    <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
      <Typography sx={{ ...labelSx, mb: 0, px: 2, pt: 2 }}>Tasks</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableHeadCellSx}>Task</TableCell>
            <TableCell sx={tableHeadCellSx}>Total</TableCell>
            <TableCell sx={tableHeadCellSx}>Entries</TableCell>
            <TableCell sx={{ ...tableHeadCellSx, width: 160 }}>Share</TableCell>
            <TableCell sx={tableHeadCellSx}>Avg / entry</TableCell>
            <TableCell sx={tableHeadCellSx}>Contributors</TableCell>
            <TableCell sx={tableHeadCellSx}>Last Activity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => {
            const pct = totalSecs > 0 ? Math.round((task.total_secs / totalSecs) * 100) : 0;
            return (
              <TableRow key={task.task_name} hover sx={{ "&:hover": { backgroundColor: "hsl(140 8% 97%)" } }}>
                <TableCell sx={tableBodyCellSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: project.color || "#ccc", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                      {task.task_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ ...tableBodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                  {formatDuration(task.total_secs)}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{task.entry_count ?? 0}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.04)", overflow: "hidden", minWidth: 60 }}>
                      <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 2, bgcolor: project.color || "#ccc", opacity: 0.7 }} />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "rgba(42,51,78,0.5)", flexShrink: 0 }}>{pct}%</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDuration(task.avg_entry_secs)}</TableCell>
                <TableCell sx={tableBodyCellSx}>{renderTaskUserAvatars(task.users) || "—"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatRelativeTime(task.last_activity_at)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function ContributorBreakdown({ project }) {
  const contributors = project.contributor_breakdown || [];
  if (contributors.length === 0) return null;

  return (
    <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
      <Typography sx={{ ...labelSx, mb: 0, px: 2, pt: 2 }}>Contributors</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableHeadCellSx}>Person</TableCell>
            <TableCell sx={tableHeadCellSx}>Time</TableCell>
            <TableCell sx={tableHeadCellSx}>Entries</TableCell>
            <TableCell sx={tableHeadCellSx}>Avg / entry</TableCell>
            <TableCell sx={tableHeadCellSx}>Last Activity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contributors.map((c) => (
            <TableRow key={c.user?.id} hover sx={{ "&:hover": { backgroundColor: "hsl(140 8% 97%)" } }}>
              <TableCell sx={tableBodyCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <UserAvatar user={c.user} size={24} tooltip={false} />
                  <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                    {c.user?.full_name || "Unknown"}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ ...tableBodyCellSx, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                {formatDuration(c.total_secs)}
              </TableCell>
              <TableCell sx={tableBodyCellSx}>{c.entry_count ?? 0}</TableCell>
              <TableCell sx={tableBodyCellSx}>{formatDuration(c.avg_entry_secs)}</TableCell>
              <TableCell sx={tableBodyCellSx}>{formatRelativeTime(c.last_activity_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const TimeSpentPanel = ({
  companyId,
  profilePipelineId,
  timeProjectId,
  period,
  customRange,
  internalUserId,
  onLoaded,
}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  const singleMode = !!timeProjectId;
  const customStart = customRange?.startDate;
  const customEnd = customRange?.endDate;

  const fetchData = useCallback(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;

    const params = { limit: 50 };
    if (companyId) params.company_id = companyId;
    if (profilePipelineId) params.profile_pipeline_id = profilePipelineId;
    if (timeProjectId) params.time_project_id = timeProjectId;
    if (period && period !== "total") {
      params.period = period;
      if (period === "custom") {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
    }
    if (internalUserId) params.internal_user_id = internalUserId;

    setLoading(true);
    sh.get("/internal/search/project_time_stats", { params })
      .then((res) => {
        const data = res.data?.data || [];
        setProjects(data);
        if (onLoaded) onLoaded(data);
      })
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [companyId, profilePipelineId, timeProjectId, period, customStart, customEnd, internalUserId, onLoaded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener("time-entry:changed", handler);
    return () => window.removeEventListener("time-entry:changed", handler);
  }, [fetchData]);

  const toggleExpand = useCallback((id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const allExpanded = projects.length > 0 && projects.every((p) => expandedRows[p.id]);
  const toggleExpandAll = useCallback(() => {
    setExpandedRows((prev) => {
      const expandAll = !projects.every((p) => prev[p.id]);
      return projects.reduce((acc, p) => ({ ...acc, [p.id]: expandAll }), {});
    });
  }, [projects]);

  if (loading) {
    return (
      <Stack spacing={3} sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={cardSx}>
                <Skeleton width="60%" height={16} />
                <Skeleton width="40%" height={32} sx={{ mt: 0.5 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box sx={cardSx}>
          <Skeleton width="100%" height={12} />
        </Box>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: "8px" }} />
        ))}
      </Stack>
    );
  }

  if (projects.length === 0) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography sx={{ color: "hsl(150 12% 38%)", fontSize: 14 }}>
          No time has been tracked for this yet.
        </Typography>
      </Box>
    );
  }

  const totalSecs = projects.reduce((sum, p) => sum + (p.total_duration_secs || 0), 0);
  const comparisonLabel = period ? COMPARISON_LABELS[period] : null;

  if (singleMode) {
    const project = projects[0];
    const entries = project.total_entries || 0;
    const avgSecs = entries > 0 ? Math.round((project.total_duration_secs || 0) / entries) : 0;
    const contributors = project.users || [];
    const comparison = project.comparison || {};

    return (
      <Stack spacing={3} sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Total Time"
              value={formatHours(project.total_duration_secs)}
              highlighted
              delta={comparison.total_secs}
              comparisonLabel={comparisonLabel}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Entries"
              value={entries}
              delta={comparison.entry_count}
              comparisonLabel={comparisonLabel}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard
              label="Contributors"
              value={contributors.length}
              footer={contributors.length > 0 ? renderTaskUserAvatars(contributors, 24) : null}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <KpiCard label="Avg / entry" value={formatDuration(avgSecs)} />
          </Grid>
        </Grid>

        <TaskTable project={project} />
        <ContributorBreakdown project={project} />
      </Stack>
    );
  }

  const weekSecs = projects.reduce((sum, p) => sum + (p.week_duration || 0), 0);
  const monthSecs = projects.reduce((sum, p) => sum + (p.month_duration || 0), 0);

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Summary Cards */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total Time" value={formatHours(totalSecs)} highlighted />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="This Week" value={formatHours(weekSecs)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="This Month" value={formatHours(monthSecs)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Projects" value={projects.length} />
        </Grid>
      </Grid>

      {/* Visual Breakdown - Stacked Bar */}
      {totalSecs > 0 && (
        <Box sx={cardSx}>
          <Typography sx={{ ...labelSx, mb: 1.5 }}>Time Distribution</Typography>
          <Box sx={{ display: "flex", height: 14, borderRadius: "7px", overflow: "hidden", mb: 2 }}>
            {projects.map((p) => (
              <Box
                key={p.id}
                sx={{
                  flex: p.total_duration_secs || 0,
                  backgroundColor: p.color || "#ccc",
                  minWidth: (p.total_duration_secs || 0) > 0 ? 4 : 0,
                  transition: "flex 0.3s ease",
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {projects.map((p) => {
              const pct = totalSecs > 0 ? Math.round(((p.total_duration_secs || 0) / totalSecs) * 100) : 0;
              return (
                <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.color || "#ccc", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                    {p.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 12% 38%)" }}>
                    {pct}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Per-Project Accordion Table */}
      <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1.5 }}>
          <Button
            onClick={toggleExpandAll}
            disableRipple
            sx={{
              minWidth: 0,
              textTransform: "none",
              fontSize: 11,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 12% 38%)",
              "&:hover": { backgroundColor: "transparent", color: "hsl(150 30% 12%)" },
            }}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...tableHeadCellSx, width: 36, px: 1 }} />
              <TableCell sx={tableHeadCellSx}>Project</TableCell>
              <TableCell sx={tableHeadCellSx}>Total</TableCell>
              <TableCell sx={tableHeadCellSx}>Entries</TableCell>
              <TableCell sx={tableHeadCellSx}>This Week</TableCell>
              <TableCell sx={tableHeadCellSx}>This Month</TableCell>
              <TableCell sx={tableHeadCellSx}>Last Activity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                expanded={!!expandedRows[p.id]}
                onToggle={() => toggleExpand(p.id)}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};

export default TimeSpentPanel;
